import { DateTime } from 'luxon'
import { WootMappedIAGDetails, OrdersWootTableRow, WootAllocationExcelRow, WootReturnExcelRow, WootMappedOrderDetails, MissionControlIA, MissionControlPriceBookEntry } from './WootOrdersImport.interfaces'
import { Inventory__c, MissionControlOrderItem } from '../common/common.interfaces'
import { getSalesforceUrlByWindowLocation } from '../common/common.entities'

export function getStatusColors(status: string){
  switch(status?.toLowerCase()){
    case 'pending':
      return {
        bg: 'warning',
        fg: 'dark'
      }
    case 'error':
      return {
        bg: 'danger',
        fg: 'white'
      }
    case 'complete':
      return {
        bg: 'success',
        fg: 'white'
      }
    default:
      return {
        bg: 'secondary',
        fg: 'white'
      }
  }
}

const WOOT_FULFILLMENT_WAREHOUSE_ID = '0016O00003TIwqRQAT'
const WOOT_COM_ACCOUNT_ID = '0013l00002GCcDaAAL'
const WOOT_PLACEHOLDER_ORDER_INTEGRATION_ID = 'a03RP00000RE86DYAT'
const STANDARD_PRICEBOOK2_ID = '01s1N0000066ylwQAA'
const FBA_US_WAREHOUSE_ID = '0011N00001jqJwOQAU'
const MAX_IA_PER_IAG = 50

export function mapWootAllocationIAGs(
  row: OrdersWootTableRow,
  sourceJson: WootAllocationExcelRow[],
  inventories: Inventory__c[]
): WootMappedIAGDetails {
  const mapped: WootMappedIAGDetails = {
    iagContent: [],
    validation: [],
    createInventory: []
  }

  const today = DateTime.now().setZone('America/Chicago')
  const utcIso = DateTime.utc().toISO()
  const baseShipmentName = `Woot FBA Flip Allocation ${row.poNumber} ${today.month}.${today.day}.${today.year} Outdoor Decor FBA`

  let currentItemAmount = 0
  let currentIag = 0
  for(const item of sourceJson){
    if(!item.ModelNumber || item.ModelNumber === ''){
      //line can be skipped
      continue
    }

    //setup IAG
    if(!mapped.iagContent[currentIag]){
      mapped.iagContent[currentIag] = {
        iag: undefined,
        ias: []
      }
      mapped.iagContent[currentIag].iag = {
        Adjustment_Reason__c: 'Warehouse Transfer',
        Shipment_Name__c: baseShipmentName,
        Source_Location__c: FBA_US_WAREHOUSE_ID,
        Target_Location__c: WOOT_FULFILLMENT_WAREHOUSE_ID,
        Status__c: 'Received',
        Date_Received__c: utcIso
      }
    }

    const relatedInventories = inventories.filter(i =>
      i.Product__r?.MPN__c?.toLowerCase() === item.ModelNumber?.toLowerCase() ||
      i.Product__r?.ASIN__c?.toLowerCase() === item.ASIN?.toLowerCase() ||
      i.Product__r?.MPN__c?.toLowerCase() === item['mfgpart#']?.toLowerCase()
    )

    const fbaWoot = relatedInventories.find(i => i.Warehouse__r?.Name === 'FBA WOOT')
    const fbaUs = relatedInventories.find(i => i.Warehouse__r?.Name === 'FBA US')
    mapped.iagContent[currentIag].ias.push({
      Main_SKU__c: fbaUs?.Product__c,
      Quantity__c: item?.Quantity,
      SKU__c: fbaUs?.Product__r?.MPN__c,
      Source_Inventory__c: fbaUs?.Id, //from FBA US
      Target_Inventory__c: fbaWoot?.Id, //to FBA WOOT
      Adjustment_Reason__c: 'Warehouse Transfer',
      Status__c: 'Received',
      Date_Received__c: utcIso
    })
    currentItemAmount += 1

    if(currentItemAmount === MAX_IA_PER_IAG){
      currentIag += 1 //increment for new iag
      currentItemAmount = 0 //reset for next 50
    }
  }

  //add # of # to the IAG names
  let iagNameIndex = 1
  for(const content of mapped.iagContent){
    content.iag.Shipment_Name__c = `${content.iag.Shipment_Name__c} ${iagNameIndex} of ${mapped.iagContent.length}`
    content.iag.Unique_External_ID__c = `Woot FBA Flip Allocation ${row.poNumber} ${iagNameIndex}`
    iagNameIndex += 1
  }

  //Do validation
  for(const content of mapped.iagContent){
    for(const ia of content.ias){
      if(!ia.Main_SKU__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Main SKU (Product ID Link)',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
      }
      if(!ia.SKU__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Product MPN (SKU)',
          sku: 'Unknown SKU'
        })
      }
      if(!ia.Quantity__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid IA Quantity',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
      }
      if(!ia.Source_Inventory__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Source Inventory',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
      }
      if(!ia.Target_Inventory__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Target Inventory',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
        const item = sourceJson.find(s => s.ModelNumber === ia.SKU__c)
        const relatedInventories = inventories.filter(i =>
          i.Product__r?.MPN__c?.toLowerCase() === item.ModelNumber?.toLowerCase() ||
          i.Product__r?.ASIN__c?.toLowerCase() === item.ASIN?.toLowerCase() ||
          i.Product__r?.MPN__c?.toLowerCase() === item['mfgpart#']?.toLowerCase()
        )
        /** Get product id from source inventory record since we should have FBA US records always? */
        const fbaUs = relatedInventories.find(i => i.Warehouse__r?.Name === 'FBA US')
        mapped.createInventory.push({
          Id: undefined,
          Fulfillable_Inventory__c: undefined,
          Live_Inventory__c: 0,
          Name: `${fbaUs.Product__r.MPN__c}_FBA WOOT`,
          Product__c: fbaUs.Product__c,
          Warehouse__c: WOOT_FULFILLMENT_WAREHOUSE_ID
        })
      }
    }
  }

  return mapped
}

export function mapWootReturnIAGs(
  row: OrdersWootTableRow,
  sourceJson: WootReturnExcelRow[],
  inventories: Inventory__c[]
): WootMappedIAGDetails {
  const mapped: WootMappedIAGDetails = {
    iagContent: [],
    validation: [],
    createInventory: []
  }

  const today = DateTime.now().setZone('America/Chicago')
  const baseShipmentName = `Woot FBA Flip Return ${row.wrpoNumber} for ${row.poNumber} ${today.month}.${today.day}.${today.year} Outdoor Decor FBA`

  let currentItemAmount = 0
  let currentIag = 0
  for(const item of sourceJson){
    /** This file only contains ASIN */
    if(!item.ASIN || item.ASIN.trim() === ''){
      //line can be skipped
      continue
    }

    const utcIso = DateTime.utc().toISO()
    //setup IAG
    if(!mapped.iagContent[currentIag]){
      mapped.iagContent[currentIag] = {
        iag: undefined,
        ias: []
      }
      mapped.iagContent[currentIag].iag = {
        Adjustment_Reason__c: 'Warehouse Transfer',
        Shipment_Name__c: baseShipmentName,
        Source_Location__c: WOOT_FULFILLMENT_WAREHOUSE_ID,
        Target_Location__c: FBA_US_WAREHOUSE_ID,
        Status__c: 'Received',
        Date_Received__c: utcIso
      }
    }

    const relatedInventories = inventories.filter(i =>
      i.Product__r?.ASIN__c?.toLowerCase() === item.ASIN?.toLowerCase()
    )

    const fbaWoot = relatedInventories.find(i => i.Warehouse__r?.Name === 'FBA WOOT')
    const fbaUs = relatedInventories.find(i => i.Warehouse__r?.Name === 'FBA US')
    mapped.iagContent[currentIag].ias.push({
      Main_SKU__c: fbaUs?.Product__c,
      Quantity__c: item?.Quantity,
      SKU__c: fbaUs?.Product__r?.MPN__c,
      Source_Inventory__c: fbaWoot?.Id, //from FBA WOOT
      Target_Inventory__c: fbaUs?.Id, //to FBA US
      Adjustment_Reason__c: 'Warehouse Transfer',
      Status__c: 'Received',
      Date_Received__c: utcIso
    })
    currentItemAmount += 1

    if(currentItemAmount === MAX_IA_PER_IAG){
      currentIag += 1 //increment for new iag
      currentItemAmount = 0 //reset for next 50
    }
  }

  //add # of # to the IAG names
  let iagNameIndex = 1
  for(const content of mapped.iagContent){
    content.iag.Shipment_Name__c = `${content.iag.Shipment_Name__c} ${iagNameIndex} of ${mapped.iagContent.length}`
    content.iag.Unique_External_ID__c = `Woot FBA Flip Return ${row.poNumber} ${iagNameIndex}`
    iagNameIndex += 1
  }

  //Do validation
  for(const content of mapped.iagContent){
    for(const ia of content.ias){
      if(!ia.Main_SKU__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Main SKU (Product ID Link)',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
      }
      if(!ia.SKU__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Product MPN (SKU)',
          sku: 'Unknown SKU'
        })
      }
      if(!ia.Quantity__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid IA Quantity',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
      }
      if(!ia.Source_Inventory__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Source Inventory',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
      }
      if(!ia.Target_Inventory__c){
        mapped.validation.push({
          shipmentName: content.iag.Shipment_Name__c,
          message: 'Invalid Target Inventory',
          sku: ia.SKU__c ?? 'Unknown SKU'
        })
      }
    }
  }

  return mapped
}

/**
 * Maps IAs from Allocation and Return IAGs to a shipped Mission Control order.
 * @param row The Azure table row
 * @param allocationIas The allocation IA's created earlier in the Journey
 * @param returnIas The return IA's created before the order 
 * @param returnJson The return JSON rows to get the cost to put on order items
 */
export function mapWootOrderFromAllocationAndReturn( 
  row: OrdersWootTableRow,
  allocationIas: MissionControlIA[],
  returnIas: MissionControlIA[],
  returnJson: WootReturnExcelRow[],
  priceBookEntries: MissionControlPriceBookEntry[],
  windowLocation: string
): WootMappedOrderDetails {
  const today = DateTime.now().setZone('America/Chicago')
  const todayUtc = DateTime.utc().toISO()
  const orderName = `Woot FBA ${row.wrpoNumber} for ${row.poNumber}`
  
  const order: WootMappedOrderDetails = {
    order: {
      Name: `${orderName} ${today.month}.${today.day}.${today.year}`,
      Unique_External_ID__c: orderName,
      AccountId: WOOT_COM_ACCOUNT_ID,
      Channel_Advisor_Shipping_Status__c: 'Shipped',
      Order_Time__c: todayUtc,
      EffectiveDate: todayUtc,
      Customer_Name__c: undefined,
      Customer_Phone_Number__c: undefined,
      Estimated_Ship_Date__c: undefined,
      Order_Discount_Amount__c: 0,
      Order_Integration__c: windowLocation.toLowerCase().includes('localhost') ? 'a03O900001ZoGt8IAF' :  WOOT_PLACEHOLDER_ORDER_INTEGRATION_ID, //TODO: Make this more dynamic
      Pricebook2Id: STANDARD_PRICEBOOK2_ID,
      Shipping_Address_Name__c: undefined,
      Shipping_Carrier_Requested__c: undefined,
      Shipping_Class_Requested__c: undefined,
      Shipping_Collected__c: 0,
      ShippingCity: undefined,
      ShippingCountry: 'US',
      BillingCountry: 'US',
      ShippingPostalCode: undefined,
      ShippingState: undefined,
      ShippingStreet: undefined,
      Status: 'Draft',
      Tax_Collected__c: 0,
      Order_Tags__c: 'WOOT-FBA'
    },
    orderItems: [],
    validation: []
  }
  for(const returnIa of returnIas){
    /** Match to allocation ia by an inventory's product id. Doesn't matter on target or source */
    const allocationIa = allocationIas.find(i => i.Main_SKU__c === returnIa.Main_SKU__c)
    const difference = allocationIa.Quantity__c - returnIa.Quantity__c
    /** If the difference is 0 (zero), then nothing was sold */
    if(difference <= 0){
      order.validation.push({
        sku: returnIa.Source_Inventory__r?.Product__r?.MPN__c,
        message: `Difference is less than, or equal to, zero. Will not be added to order. [ ${difference} ]`,
        shipmentName: returnIa.Inventory_Adjustment_Group__r.Shipment_Name__c
      })
      continue //onto the next return ia
    }

    /** The difference is not <= 0 so we create an order item for it */
    const returnItem = returnJson.find(r => r.ASIN?.toLowerCase() === returnIa.Main_SKU__r?.ASIN__c?.toLowerCase())
    const pricebook = priceBookEntries.find(pe => pe.Product2Id === returnIa.Main_SKU__c)
    order.orderItems.push({
      Line_Item_Title__c: returnItem.Description,
      Listing__c: undefined,
      PricebookEntryId: pricebook.Id,
      Product2Id: returnIa.Main_SKU__c,
      Quantity: difference,
      UnitPrice: returnItem.DirectUnitCostExclTax,
      External_Order_Line_ID__c: returnItem.No,
      Fulfillment_Warehouse__c: WOOT_FULFILLMENT_WAREHOUSE_ID,
      Inventory__c: returnIa.Source_Inventory__c
    })
  }

  return order
}

export function generateWootFbaEmailBody(
  row: OrdersWootTableRow,

): string {
  const order = JSON.parse(row.returnOrder)
  return `
<div>
  <div>
    A WOOT! FBA event has been completed. Attached are the PDF and Excel files sent from WOOT! containing the original FBA purchase order ${row.poNumber.replace('PO', 'PO ')} and return FBA purchase order ${row.wrpoNumber.replace('WRPO', 'RPO ')} documentation.
    <br/><br/>
    Original Purchase Order IA Groups:<br/>
    ${JSON.parse(row.allocationIags).map(iag => {
      return `<div>
        <a target="_blank" rel="noopener noreferrer" href="${getSalesforceUrlByWindowLocation(window.location.href)}/${iag.Id}">${iag.Shipment_Name__c}</a>
        <br/>
      </div>`
    })}
    <br/>
    Return Purchase Order IA Groups:<br/>
    ${JSON.parse(row.returnIags).map(iag => {
      return `<div>
        <a target="_blank" rel="noopener noreferrer" href="${getSalesforceUrlByWindowLocation(window.location.href)}/${iag.Id}">${iag.Shipment_Name__c}</a>
        <br/>
      </div>`
    })}
    <br/>
    Return Purchase Order Salesforce Order & Order Items:<br/>
    <a target="_blank" rel="noopener noreferrer" href="${getSalesforceUrlByWindowLocation(window.location.href)}/${order.Id}">${order.Name}</a>
    <br/>
    <br/>
    Buy-From Vendor # VND04410
    <br/>
    ${row.wrpoNumber.replace('WRPO', 'RPO ')} against ${row.poNumber.replace('PO', 'PO ')}
    <br/>
    <br/>
    Please create and send an invoice to <a href="mailto:accountspayable@woot.com">accountspayable@woot.com</a>
  </div>
</div>
`
}